import React, { useState, useEffect } from "react";
import { useSession } from "../../providers/SessionProvider";
import { useAuth } from "../../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { clientsApi, comptesApi } from "../../api/clients.api";
import { authApi } from "../../api";
import {
  User,
  Globe,
  Info,
  MapPin,
  HelpCircle,
  Share2,
  LogOut,
  Camera,
  Eye,
  EyeOff,
  X,
  Check,
  UserPen,
  ChevronRight,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  UserLock,
  Lock,
  ShieldCheck,
  Mail,
} from "lucide-react";
import fondImageDesktop from "../../components/profileUser/assets/Fond-Large.jpg";
import fondImageMobile from "../../components/profileUser/assets/Fond.jpg";
import { toast } from "../../hooks/use-toast";
import { SecuritySettings } from "../../components/profileUser/SecuritySettings";

// Données mockées
const mockUserData = {
  firstName: "Jean",
  lastName: "Dupont",
  fullName: "Jean Dupont",
  image: null,
  kycStatus: "verified", // verified | unverified | processing | rejected
  isTestAccount: false,
};

// Modal de confirmation générique
const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
}) => {
  const { t } = useTranslation(['common']);
  const finalCancelText = cancelText || t('common:cancel');
  const finalConfirmText = confirmText || t('common:confirm');
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center pointer-events-none">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm pointer-events-auto"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white rounded-t-2xl md:rounded-2xl p-5 w-full md:max-w-sm border-t md:border border-gray-100 shadow-xl pointer-events-auto z-10"
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-5">{message}</p>
            <div className="flex gap-2.5">
              <button
                onClick={onClose}
                className="flex-1 py-2 px-3 border border-gray-300 rounded-lg text-gray-700 font-medium text-xs"
              >
                {finalCancelText}
              </button>
              <button
                onClick={onConfirm}
                className="flex-1 py-2 px-3 bg-red-600 text-white rounded-lg font-semibold text-xs"
              >
                {finalConfirmText}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

const availableLanguages = [
  { code: "fr", name: "Français", nativeName: "Français", flag: "🇫🇷" },
  { code: "en", name: "English", nativeName: "English", flag: "🇬🇧" },
  { code: "ht", name: "Kreyòl", nativeName: "Kreyòl Ayisyen", flag: "🇭🇹" },
  { code: "es", name: "Español", nativeName: "Español", flag: "🇪🇸" },
];

// Composant pour le statut KYC
const KYCStatusBadge = ({ status, onClick }) => {
  const { t } = useTranslation(['settings']);
  const statusConfig = {
    verified: {
      icon: CheckCircle,
      text: t('settings:kyc.verified'),
      color: "text-green-600",
      bgColor: "bg-green-50",
    },
    unverified: {
      icon: AlertCircle,
      text: t('settings:kyc.unverified'),
      color: "text-orange-600",
      bgColor: "bg-orange-50",
    },
    processing: {
      icon: Clock,
      text: t('settings:kyc.processing'),
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    rejected: {
      icon: XCircle,
      text: t('settings:kyc.rejected'),
      color: "text-red-600",
      bgColor: "bg-red-50",
    },
  };

  const config = statusConfig[status] || statusConfig.unverified;
  const Icon = config.icon;

  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${config.bgColor}`}
    >
      <Icon size={13} className={config.color} />
      <span className={`text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    </motion.button>
  );
};

// Modal de vérification KYC
const KYCVerificationModal = ({ isOpen, onClose, verificationUrl, onComplete }) => {
  const { t } = useTranslation(['settings', 'common']);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen && verificationUrl) {
      setIsLoading(true);
    }
  }, [isOpen, verificationUrl]);

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative bg-white rounded-2xl w-full max-w-2xl h-[85vh] md:h-[80vh] mx-4 shadow-2xl overflow-hidden z-10 flex flex-col"
          >
            {/* Header */}
            <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-3">
                <ShieldCheck className="text-blue-600" size={22} />
                <h3 className="text-lg font-bold text-gray-800">
                  {t('settings:kyc.verificationTitle')}
                </h3>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X size={22} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 relative">
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                  <div className="flex flex-col items-center gap-3">
                    <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent" />
                    <p className="text-sm text-gray-600">{t('settings:kyc.loading')}</p>
                  </div>
                </div>
              )}
              {verificationUrl && (
                <iframe
                  src={verificationUrl}
                  className="w-full h-full border-0"
                  onLoad={handleIframeLoad}
                  title="Vérification KYC"
                  allow="camera; microphone"
                />
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                {t('settings:kyc.secureByDidit')}
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('common:close')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

// Modal pour modifier les informations personnelles
const EditInfoModal = ({ isOpen, onClose, userData, canEditIdentity, onUpdate }) => {
  const { t } = useTranslation(['settings', 'common', 'profile']);
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    dateNaissance: "",
    adresse: "",
    ville: "",
    pays: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  // Initialize form with user data when modal opens
  React.useEffect(() => {
    console.log("EditInfoModal Open:", isOpen, "UserData:", userData);
    if (isOpen && userData) {
      const dataToSet = {
        nom: userData.nom || "",
        prenom: userData.prenom || "",
        dateNaissance: userData.dateNaissance ? userData.dateNaissance.split('T')[0] : "",
        adresse: userData.adresse || "",
        ville: userData.ville || "",
        pays: userData.pays || "",
      };
      console.log("Setting form data:", dataToSet);
      setFormData(dataToSet);
    }
  }, [isOpen, userData]);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      // Filter out empty strings to avoid validation errors (especially for date)
      const sanitizedData = Object.fromEntries(
        Object.entries(formData).filter(([_, v]) => v !== "" && v !== null && v !== undefined)
      );
      await onUpdate(sanitizedData);
      onClose();
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      toast({
        title: "Erè",
        description: "Erreur lors de la mise à jour du profil.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center pointer-events-none">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm pointer-events-auto"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white rounded-t-2xl md:rounded-2xl p-6 w-full md:max-w-md border-t md:border border-gray-100 shadow-xl max-h-[85vh] overflow-y-auto pointer-events-auto z-10"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-800">
                {t('settings:profile.editTitle')}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              {!canEditIdentity && (
                <div className="bg-blue-50 p-3 rounded-lg flex items-start gap-2 text-sm text-blue-700 mb-2">
                  <Info size={16} className="mt-0.5 shrink-0" />
                  <p>
                    {t('settings:profile.verifiedAlert')}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('settings:profile.firstName')}</label>
                  <input
                    type="text"
                    value={formData.prenom}
                    onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                    disabled={!canEditIdentity}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 ${!canEditIdentity ? "bg-gray-100 text-gray-500 border-gray-200" : "border-gray-300"
                      }`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('settings:profile.lastName')}</label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    disabled={!canEditIdentity}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 ${!canEditIdentity ? "bg-gray-100 text-gray-500 border-gray-200" : "border-gray-300"
                      }`}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('settings:profile.birthDate')}</label>
                <input
                  type="date"
                  value={formData.dateNaissance}
                  onChange={(e) => setFormData({ ...formData, dateNaissance: e.target.value })}
                  disabled={!canEditIdentity}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 ${!canEditIdentity ? "bg-gray-100 text-gray-500 border-gray-200" : "border-gray-300"
                    }`}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('settings:profile.address')}</label>
                <input
                  type="text"
                  value={formData.adresse}
                  onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                  disabled={!canEditIdentity} // Assuming address is also locked after KYC, or should it be editable? Plan said "only allow imageUrl update for KYC verified"
                  className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 ${!canEditIdentity ? "bg-gray-100 text-gray-500 border-gray-200" : "border-gray-300"
                    }`}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('settings:profile.city')}</label>
                  <input
                    type="text"
                    value={formData.ville}
                    onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                    disabled={!canEditIdentity}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 ${!canEditIdentity ? "bg-gray-100 text-gray-500 border-gray-200" : "border-gray-300"
                      }`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('settings:profile.country')}</label>
                  <input
                    type="text"
                    value={formData.pays}
                    onChange={(e) => setFormData({ ...formData, pays: e.target.value })}
                    disabled={!canEditIdentity}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 ${!canEditIdentity ? "bg-gray-100 text-gray-500 border-gray-200" : "border-gray-300"
                      }`}
                  />
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSubmit}
                disabled={isLoading || (!canEditIdentity)} // If everything is locked, disable save? Actually imageUrl is handled separately. If identity is locked, this form is read-only.
                className={`w-full py-3 rounded-lg font-semibold transition-colors flex items-center justify-center ${isLoading || !canEditIdentity
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                ) : !canEditIdentity ? (
                  t('settings:profile.readOnlyKyc')
                ) : (
                  t('settings:profile.saveChanges')
                )}
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
const ResetPINModal = ({ isOpen, onClose, email }) => {
  const { t } = useTranslation(['settings', 'common']);
  const [isLoading, setIsLoading] = useState(false);

  // Mask email for privacy: john.doe@example.com → j***@example.com
  const maskEmail = (emailStr) => {
    if (!emailStr || !emailStr.includes('@')) return t('settings:profile.yourEmail');
    const [localPart, domain] = emailStr.split('@');
    const maskedLocal = localPart.length > 1 ? localPart[0] + '***' : localPart + '***';
    return `${maskedLocal}@${domain}`;
  };

  const handleConfirm = async () => {
    if (!email) {
      toast({ title: t('common:error'), description: t('settings:noEmailOrPhone'), variant: "destructive" });
      return;
    }
    setIsLoading(true);
    try {
      const res = await authApi.forgotPin({ identifier: email });
      toast({ title: t('settings:resetPinSuccess'), description: res.message || t('settings:checkEmail'), variant: "default" });
      onClose();
    } catch (error) {
      console.error(error);
      toast({ title: t('common:error'), description: t('settings:resetPinError'), variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center pointer-events-none">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm pointer-events-auto"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white rounded-t-2xl md:rounded-2xl w-full md:max-w-md shadow-xl overflow-hidden pointer-events-auto z-10"
          >
            {/* Header with color and icon */}
            <div className="bg-[#FFF9E5] pt-8 pb-8 flex justify-center items-center relative">
              <div className="bg-[#FFA000] p-4 rounded-full shadow-lg z-10">
                <div className="bg-white/20 p-2 rounded-full">
                  <Lock className="text-white" size={32} />
                </div>
              </div>
              {/* Decorative circle/curve could go here but keeping it simple for now */}
            </div>

            <div className="p-6 pt-4 text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t('settings:resetPinTitle')}
              </h3>

              <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                {t('settings:resetPinConfirmMessage', { email: maskEmail(email) })}
              </p>

              <div className="bg-[#FFF9E5] rounded-xl p-4 mb-8 flex items-start gap-3 text-left">
                <Info className="text-[#FFA000] shrink-0 mt-0.5" size={20} />
                <p className="text-[#B27000] text-sm font-medium">
                  {t('settings:checkEmail')}
                </p>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={onClose}
                  className="flex-1 py-3.5 px-4 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={isLoading}
                  className="flex-1 py-3.5 px-4 bg-[#FFA000] text-white rounded-xl font-semibold hover:bg-[#E69000] transition-colors disabled:opacity-70 flex items-center justify-center"
                >
                  {isLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  ) : (
                    t('common:confirm')
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

// ... existing language modal ...

// Composant pour changer la langue
const LanguageModal = ({
  isOpen,
  onClose,
  currentLanguage,
  onLanguageChange,
}) => {
  const { t } = useTranslation(['settings', 'common']);
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center pointer-events-none">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm pointer-events-auto"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white rounded-t-2xl md:rounded-2xl p-5 w-full md:max-w-sm border-t md:border border-gray-100 shadow-xl max-h-[85vh] overflow-y-auto pointer-events-auto z-10"
          >
            <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Globe size={20} className="text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-800">
                  {t('settings:selectLanguage')}
                </h3>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-2">
              {availableLanguages.map((lang) => (
                <motion.button
                  key={lang.code}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    onLanguageChange(lang.code);
                    onClose();
                  }}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${currentLanguage === lang.code
                    ? "bg-blue-50 border-blue-600"
                    : "bg-transparent border-gray-100 hover:bg-gray-50"
                    }`}
                >
                  <span className="text-2xl">{lang.flag}</span>
                  <div className="flex-1 text-left">
                    <div
                      className={`text-sm font-semibold ${currentLanguage === lang.code
                        ? "text-blue-600"
                        : "text-gray-700"
                        }`}
                    >
                      {lang.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {lang.nativeName}
                    </div>
                  </div>
                  {currentLanguage === lang.code && (
                    <div className="bg-blue-600 rounded-full p-1">
                      <Check size={14} className="text-white" />
                    </div>
                  )}
                </motion.button>
              ))}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};




// Composant pour un élément de menu
const MenuItem = ({
  icon: Icon,
  title,
  subtitle,
  onClick,
  disabled = false,
  showArrow = true,
}) => {
  return (
    <motion.button
      whileHover={!disabled ? { x: 4 } : {}}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`w-full flex items-center justify-between py-3 ${disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
    >
      <div className="flex items-center gap-4">
        <Icon size={22} className="text-gray-700" />
        <div className="text-left">
          <div className="text-sm font-medium text-gray-800">{title}</div>
          {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
        </div>
      </div>
      {showArrow && <ChevronRight size={18} className="text-gray-400" />}
    </motion.button>
  );
};

// Composant principal
const Profile = () => {
  const { t, i18n } = useTranslation(['settings', 'common', 'profile']);
  const [userData, setUserData] = useState(null); // Initial state null while loading
  const [canEditIdentity, setCanEditIdentity] = useState(false);
  const [securityQuestionsConfigured, setSecurityQuestionsConfigured] = useState(true); // Default to true to hide banner until loaded

  const currentLanguage = i18n.language;
  const [editInfoModalOpen, setEditInfoModalOpen] = useState(false);
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [languageModalOpen, setLanguageModalOpen] = useState(false);
  const [kycModalOpen, setKycModalOpen] = useState(false);
  const [kycVerificationUrl, setKycVerificationUrl] = useState(null);
  const [isKycLoading, setIsKycLoading] = useState(false);

  const [isLoading, setIsLoading] = useState(true); // Loading initially true
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  const { userData: sessionData, refreshUserData } = useSession();
  const { logout, user } = useAuth();

  useEffect(() => {
    if (sessionData && sessionData.accounts && sessionData.accounts.length > 0) {
      const firstAccount = sessionData.accounts[0];

      // Map backend kycvalidation to UI kycStatus
      const apiKycStatus = firstAccount.client?.kycStatus || firstAccount.kycvalidation || 'not_started';
      let uiKycStatus = 'unverified';
      if (apiKycStatus === 'verified') uiKycStatus = 'verified';
      else if (apiKycStatus === 'pending') uiKycStatus = 'processing';
      else if (apiKycStatus === 'rejected') uiKycStatus = 'rejected';

      const mappedData = {
        id: firstAccount.client_id,
        nom: firstAccount.client?.nom || firstAccount.nom || "",
        prenom: firstAccount.client?.prenom || firstAccount.prenom || "",
        fullName: `${firstAccount.client?.prenom || firstAccount.prenom || ""} ${firstAccount.client?.nom || firstAccount.nom || ""}`,
        email: firstAccount.client?.email || firstAccount.email,
        telephone: firstAccount.client?.telephone || firstAccount.tel,
        image: firstAccount.client?.imageUrl || firstAccount.image,
        kycStatus: uiKycStatus,
        statut: user?.statut || firstAccount.client?.statut || firstAccount.statut || "active",
        accountCode: firstAccount.code,
        isTestAccount: false,
      };
      setUserData(mappedData);
      setCanEditIdentity(uiKycStatus !== 'verified');
    }
  }, [sessionData, user, refreshUserData]);

  // Loading state derived from context
  useEffect(() => {
    setIsLoading(sessionData.isLoading);
  }, [sessionData.isLoading]);

  /*
  useEffect(() => {
    fetchProfileData();
  }, []);
  */

  const fetchProfileData = async () => {
    // Deprecated - logic moved to SessionProvider
    if (!sessionData.accounts || sessionData.accounts.length === 0) {
      refreshUserData();
    }
  };

  const handleUpdateProfile = async (data) => {
    const res = await clientsApi.updateMyProfile(data);
    if (res.success) {
      // Refresh local data
      setUserData({
        ...userData,
        ...res.client,
        fullName: `${res.client.prenom} ${res.client.nom}`,
        image: res.client.imageUrl,
      });
      // Refresh global cache
      await refreshUserData();

      toast({
        title: t('common:success'),
        description: res.message || t('settings:success.profileUpdated'),
        type: "success",
      });
    }
  };

  const handleLogout = () => {
    logout();
  };

  // KYC Verification handlers
  const handleStartKycVerification = async () => {
    const username = userData?.email || userData?.telephone;
    if (!username) {
      toast({
        title: t('common:error'),
        description: t('settings:noEmailOrPhone'),
        type: "error",
      });
      return;
    }

    setIsKycLoading(true);
    try {
      const response = await clientsApi.startKycVerification(username);
      if (response.success && response.verification_url) {
        setKycVerificationUrl(response.verification_url);
        setKycModalOpen(true);
        // Start polling for status
        startKycPolling(username);
      } else {
        toast({
          title: t('common:error'),
          description: t('settings:kyc.startError'),
          type: "error",
        });
      }
    } catch (error) {
      console.error("KYC Error:", error);
      toast({
        title: t('common:error'),
        description: error?.response?.data?.message || t('settings:kyc.error'),
        type: "error",
      });
    } finally {
      setIsKycLoading(false);
    }
  };

  const kycPollIntervalRef = React.useRef(null);

  const startKycPolling = (username) => {
    // Clear any existing interval
    if (kycPollIntervalRef.current) {
      clearInterval(kycPollIntervalRef.current);
    }

    kycPollIntervalRef.current = setInterval(async () => {
      try {
        const status = await clientsApi.checkKycStatus(username);
        if (status.kyc_status === 'verified' || status.kyc_status === 'rejected') {
          clearInterval(kycPollIntervalRef.current);
          kycPollIntervalRef.current = null;
          setKycModalOpen(false);
          setKycVerificationUrl(null);

          if (status.kyc_status === 'verified') {
            toast({
              title: t('common:success'),
              description: t('settings:kyc.successTitle'),
              type: "success",
            });
          } else {
            toast({
              title: t('settings:kyc.rejected'),
              description: t('settings:kyc.failedTitle'),
              type: "error",
            });
          }
          // Refresh user data
          await refreshUserData();
        }
      } catch (error) {
        console.error("KYC polling error:", error);
      }
    }, 3000); // Poll every 3 seconds
  };

  const handleCloseKycModal = () => {
    if (kycPollIntervalRef.current) {
      clearInterval(kycPollIntervalRef.current);
      kycPollIntervalRef.current = null;
    }
    setKycModalOpen(false);
    setKycVerificationUrl(null);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (kycPollIntervalRef.current) {
        clearInterval(kycPollIntervalRef.current);
      }
    };
  }, []);

  const currentLangData = availableLanguages.find(
    (lang) => lang.code === currentLanguage
  );

  const bannerTranslations = {
    fr: {
      pendingTitle: "Compte en attente",
      pendingMessage: "Validation en cours...",
      contactMessage: "Bonjour, je souhaite valider mon compte. Nom : {{name}}, Compte : {{accountNumber}}"
    },
    en: {
      pendingTitle: "Account Pending",
      pendingMessage: "Validation in progress...",
      contactMessage: "Hello, I would like to validate my account. Name: {{name}}, Account: {{accountNumber}}"
    },
    ht: {
      pendingTitle: "Kont an atant",
      pendingMessage: "Validasyon ap fèt...",
      contactMessage: "Bonjou, mwen ta renmen valide kont mwen an. Non: {{name}}, Kont: {{accountNumber}}"
    },
    es: {
      pendingTitle: "Cuenta pendiente",
      pendingMessage: "Validación en curso...",
      contactMessage: "Hola, me gustaría validar mi cuenta. Nombre: {{name}}, Cuenta: {{accountNumber}}"
    }
  };

  const handleContactSupport = () => {
    const lang = currentLanguage || 'fr';
    const t = bannerTranslations[lang] || bannerTranslations.fr;
    const fullName = userData?.fullName || '';
    const accountNum = userData?.accountCode || userData?.telephone || '';

    const message = t.contactMessage
      .replace('{{name}}', fullName)
      .replace('{{accountNumber}}', accountNum);

    const url = `https://wa.me/50940128282?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  if (!userData && isLoading) {
    return (
      <div className="min-h-screen relative flex items-center justify-center">
        <div
          className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat -z-10 md:hidden"
          style={{
            backgroundImage: `url(${fondImageMobile})`,
          }}
        />
        <div
          className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat -z-10 hidden md:block"
          style={{
            backgroundImage: `url(${fondImageDesktop})`,
          }}
        />
        <div className="bg-white/80 backdrop-blur-md p-6 rounded-2xl shadow-xl flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent" />
          <p className="text-sm font-medium text-gray-600">{t('common:loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <div
        className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat -z-10 md:hidden"
        style={{
          backgroundImage: `url(${fondImageMobile})`,
        }}
      />
      <div
        className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat -z-10 hidden md:block"
        style={{
          backgroundImage: `url(${fondImageDesktop})`,
        }}
      />
      <div className="px-6 md:px-16 lg:px-32 md:mt-6 pt-4">
        <div className="max-w-2xl mx-auto space-y-6">

          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-3">
              {t('settings:accountSettings')}
            </h3>

            {/* KYC Status Banner - Show if not verified */}
            {userData?.kycStatus !== 'verified' && (
              <div className="mb-4">
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={handleStartKycVerification}
                  disabled={isKycLoading || userData?.kycStatus === 'processing'}
                  className={`w-full p-4 rounded-xl border flex items-center justify-between transition-all ${userData?.kycStatus === 'processing'
                    ? 'bg-blue-50 border-blue-200'
                    : userData?.kycStatus === 'rejected'
                      ? 'bg-red-50 border-red-200'
                      : 'bg-orange-50 border-orange-200'
                    }`}
                >
                  <div className="flex items-center gap-3">
                    {userData?.kycStatus === 'processing' ? (
                      <Clock className="text-blue-600" size={20} />
                    ) : userData?.kycStatus === 'rejected' ? (
                      <XCircle className="text-red-600" size={20} />
                    ) : (
                      <AlertCircle className="text-orange-600" size={20} />
                    )}
                    <div className="text-left">
                      <p className={`text-sm font-semibold ${userData?.kycStatus === 'processing'
                        ? 'text-blue-700'
                        : userData?.kycStatus === 'rejected'
                          ? 'text-red-700'
                          : 'text-orange-700'
                        }`}>
                        {userData?.kycStatus === 'processing'
                          ? t('settings:kyc.processing')
                          : userData?.kycStatus === 'rejected'
                            ? t('settings:kyc.rejected')
                            : t('settings:kyc.unverified')}
                      </p>
                      <p className="text-xs text-gray-600">
                        {userData?.kycStatus === 'processing'
                          ? t('settings:kyc.processingDesc')
                          : t('settings:kyc.startVerification')}
                      </p>
                    </div>
                  </div>
                  {userData?.kycStatus !== 'processing' && (
                    <ChevronRight className={`${userData?.kycStatus === 'rejected' ? 'text-red-500' : 'text-orange-500'
                      }`} size={18} />
                  )}
                </motion.button>
              </div>
            )}

            <div className="bg-white/50 backdrop-blur-sm rounded-2xl p-4 divide-y divide-gray-200 border border-gray-100">
              <MenuItem
                icon={UserPen}
                title={t('settings:modifyInformation')}
                subtitle={t('settings:profile.editSubtitle')}
                onClick={() => setEditInfoModalOpen(true)}
              />

              <div className="py-3">
                <div className="flex items-center justify-between">
                  <MenuItem
                    icon={UserLock}
                    title={t('settings:resetPin')}
                    subtitle={t('settings:resetPinSubtitle')}
                    onClick={() => setPinModalOpen(true)}
                    showArrow={true}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="md:grid md:grid-cols-2 md:gap-6">
            <div className="mb-6 md:mb-0 md:hidden">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">
                {t('settings:language')}
              </h3>
              <div className="bg-white/50 backdrop-blur-sm rounded-2xl p-4 border border-gray-100">
                <motion.button
                  whileHover={{ x: 4 }}
                  onClick={() => setLanguageModalOpen(true)}
                  className="w-full flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <Globe size={22} className="text-gray-700" />
                    <span className="text-sm font-medium text-gray-800">
                      {t('settings:changeLanguage')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{currentLangData?.flag}</span>
                    <span className="text-xs font-semibold text-gray-600">
                      {currentLangData?.name}
                    </span>
                    <ChevronRight size={18} className="text-gray-400" />
                  </div>
                </motion.button>
              </div>
            </div>

          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-3">
              {t('settings:security.title')}
            </h3>
            <SecuritySettings />
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-3">
              {t('settings:about')}
            </h3>
            <div className="bg-white/50 backdrop-blur-sm rounded-2xl p-4 divide-y divide-gray-200 border border-gray-100">
              <MenuItem
                icon={Info}
                title={t('settings:aboutBousanm')}
                onClick={() => console.log("About")}
              />
              <MenuItem
                icon={MapPin}
                title={t('settings:ourOffice')}
                onClick={() => console.log("Offices")}
              />
              <MenuItem
                icon={HelpCircle}
                title={t('settings:faqs')}
                onClick={() => console.log("FAQ")}
              />
              <MenuItem
                icon={Share2}
                title={t('settings:shareWithFriends')}
                onClick={() => console.log("Share")}
              />
            </div>
          </div>


          <div className="space-y-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => console.log("Terms")}
              className="w-full text-center"
            >
              <span className="text-sm text-blue-600 underline">
                {t('settings:termsConditions')}
              </span>
            </motion.button>

            <motion.button
              onClick={() => setLogoutConfirmOpen(true)}
              className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2"
            >
              <span>{t('settings:logout')}</span>
              <LogOut size={16} />
            </motion.button>
          </div>

        </div>
      </div>

      {/* Modals */}
      <ResetPINModal
        isOpen={pinModalOpen}
        onClose={() => setPinModalOpen(false)}
        email={userData?.email}
      />
      <EditInfoModal
        isOpen={editInfoModalOpen}
        onClose={() => setEditInfoModalOpen(false)}
        userData={userData}
        canEditIdentity={canEditIdentity}
        onUpdate={handleUpdateProfile}
      />

      {/* SecurityModal removed as requested */}

      <LanguageModal
        isOpen={languageModalOpen}
        onClose={() => setLanguageModalOpen(false)}
        currentLanguage={currentLanguage}
        onLanguageChange={(lng) => {
          i18n.changeLanguage(lng);
          toast({
            title: t('common:success'),
            description: t('common:languageChanged'),
            type: "success",
          });
        }}
      />

      {/* KYC Verification Modal */}
      <KYCVerificationModal
        isOpen={kycModalOpen}
        onClose={handleCloseKycModal}
        verificationUrl={kycVerificationUrl}
      />




      <ConfirmModal
        isOpen={logoutConfirmOpen}
        onClose={() => setLogoutConfirmOpen(false)}
        onConfirm={() => {
          setLogoutConfirmOpen(false);
          handleLogout();
        }}
        title={t('settings:logout')}
        message={t('settings:logoutConfirm')}
        confirmText={t('settings:logout')}
        cancelText={t('common:cancel')}
      />

      {/* Loading Overlay */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
          >
            <div className="bg-white rounded-2xl p-6 flex flex-col items-center">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent mb-4" />
              <p className="text-gray-800 font-semibold">{t('common:loading')}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Profile;
